import FocusTrap from 'focus-trap-react'
import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import { ThemeProvider } from 'styled-components'

import { THEME } from '../../styles'
import useOnClickOutside from '../../hooks/useClickOutside'
import { useScrollLock } from '../../contexts/ScrollLockContext'

import Styled from './Drawer.styled'

const Drawer = ({
  children,
  className,
  drawerId,
  isOpened,
  maxWidth,
  onClose,
  onGoBack,
  shouldCloseOnClickOutside,
  variant,
}) => {
  const { addScrollLocker, removeScrollLocker, scrollLockers } = useScrollLock()
  const drawerName = useMemo(() => `drawers_${drawerId}`, [drawerId])
  useEffect(() => {
    if (isOpened) {
      addScrollLocker(drawerName)
    } else {
      removeScrollLocker(drawerName)
    }
  }, [isOpened, addScrollLocker, removeScrollLocker, drawerName])
  useEffect(() => {
    return () => {
      removeScrollLocker(drawerName)
    }
  }, [drawerName]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleClickOutside = useCallback(() => {
    if (shouldCloseOnClickOutside && onClose && isOpened) {
      onClose()
    }
  }, [isOpened, onClose, shouldCloseOnClickOutside])
  const contentRef = useRef(null)
  useOnClickOutside(contentRef, handleClickOutside)

  return (
    <ThemeProvider theme={{ variant }}>
      <Styled className={className} isOpened={isOpened}>
        <Styled.overlay isOpened={isOpened}>
          <Styled.drawerContainer maxWidth={maxWidth}>
            <Styled.drawer isOpened={isOpened}>
              <FocusTrap
                active={isOpened}
                paused={scrollLockers.length > 1}
                focusTrapOptions={{
                  fallbackFocus: () => contentRef.current,
                  initialFocus: false,
                  clickOutsideDeactivates: false,
                  allowOutsideClick: true,
                  escapeDeactivates: false,
                }}
              >
                <Styled.drawerLayout
                  ref={contentRef}
                  role="presentation"
                  tabIndex={-1}
                >
                  {onGoBack ? (
                    <Styled.goBackButton
                      role="button"
                      tabIndex={0}
                      onClick={onGoBack}
                      isOpened={isOpened}
                    >
                      <Styled.goBackIcon
                        name="arrow-left-32"
                        height={32}
                        width={32}
                        fill={THEME.COLORS.foreground.secondary}
                      />
                    </Styled.goBackButton>
                  ) : null}
                  <Styled.drawerContent isOpened={isOpened}>
                    {children}
                  </Styled.drawerContent>
                  {onClose ? (
                    <Styled.closeButton
                      role="button"
                      tabIndex={0}
                      onClick={onClose}
                      isOpened={isOpened}
                    >
                      <Styled.closeIcon
                        name="cross-32"
                        height={32}
                        width={32}
                        fill={THEME.COLORS.foreground.secondary}
                      />
                    </Styled.closeButton>
                  ) : null}
                </Styled.drawerLayout>
              </FocusTrap>
            </Styled.drawer>
          </Styled.drawerContainer>
        </Styled.overlay>
      </Styled>
    </ThemeProvider>
  )
}

Drawer.defaultProps = {
  drawerId: 'drawer',
  isOpened: false,
  onClose: () => {},
  shouldCloseOnClickOutside: true,
  variant: 'white',
}

export default Drawer
